import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { BrandLogo } from '../BrandLogo';
import { API_REFERENCE_URL } from '../../pages/docs/samples';

/**
 * Navbar and footer of the public pages — the landing page and the developer documentation. The
 * React counterpart of web/Pages/Shared/_LandingLayout.cshtml.
 *
 * `onLanding` keeps the section links as bare fragments ("#pricing") on the landing page itself;
 * everywhere else they point back to it ("/#pricing").
 */

const section = (onLanding: boolean, hash: string) => (onLanding ? hash : `/${hash}`);

export function MarketingNav({ onLanding = false }: { onLanding?: boolean }) {
  const { isAuthenticated } = useAuth();
  // Bootstrap's collapse plugin is not loaded (see Dropdown.tsx for the same reason), so the
  // mobile navbar toggle is React state rather than data-bs-toggle.
  const [navOpen, setNavOpen] = useState(false);
  const close = () => setNavOpen(false);

  return (
    <nav className="navbar navbar-expand-lg landing-navbar fixed-top">
      <div className="container">
        <Link className="navbar-brand d-flex align-items-center" to="/" aria-label="InertiaID home">
          <BrandLogo size={34} />
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          aria-expanded={navOpen}
          aria-label="Toggle navigation"
          onClick={() => setNavOpen((v) => !v)}
        >
          <span className="navbar-toggler-icon" />
        </button>
        <div className={`collapse navbar-collapse ${navOpen ? 'show' : ''}`}>
          <ul className="navbar-nav ms-auto align-items-center gap-1">
            <li className="nav-item"><a className="nav-link" href={section(onLanding, '#features')} onClick={close}>Features</a></li>
            <li className="nav-item"><a className="nav-link" href={section(onLanding, '#how-it-works')} onClick={close}>How It Works</a></li>
            <li className="nav-item"><a className="nav-link" href={section(onLanding, '#pricing')} onClick={close}>Pricing</a></li>
            <li className="nav-item"><a className="nav-link" href={section(onLanding, '#contact')} onClick={close}>Contact</a></li>
            <li className="nav-item"><Link className="nav-link" to="/demo" onClick={close}>Demo</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/docs" onClick={close}>Docs</Link></li>
            {isAuthenticated ? (
              <li className="nav-item ms-2">
                <Link to="/dashboard" className="btn btn-primary btn-sm px-3" onClick={close}>Dashboard →</Link>
              </li>
            ) : (
              <>
                <li className="nav-item ms-2">
                  <Link to="/login" className="nav-link" onClick={close}>Log in</Link>
                </li>
                <li className="nav-item ms-1">
                  <Link to="/signup" className="btn btn-primary btn-sm px-3" onClick={close}>Sign up</Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}

export function MarketingFooter({ onLanding = false }: { onLanding?: boolean }) {
  return (
    <footer className="landing-footer py-5">
      <div className="container">
        <div className="row g-4">
          <div className="col-lg-4">
            <div className="mb-3">
              <BrandLogo size={34} />
            </div>
            <p className="text-body-secondary small">
              Enterprise-grade identity verification powered by AI.
              Trusted by financial institutions, fintechs, and regulated businesses worldwide.
            </p>
          </div>
          <div className="col-6 col-lg-2">
            <h6 className="fw-semibold mb-3">Product</h6>
            <ul className="list-unstyled text-body-secondary small">
              <li className="mb-1"><a href={section(onLanding, '#features')} className="text-body-secondary text-decoration-none">Features</a></li>
              <li className="mb-1"><a href={section(onLanding, '#pricing')} className="text-body-secondary text-decoration-none">Pricing</a></li>
              <li className="mb-1"><Link to="/demo" className="text-body-secondary text-decoration-none">Live demo</Link></li>
            </ul>
          </div>
          <div className="col-6 col-lg-2">
            <h6 className="fw-semibold mb-3">Developers</h6>
            <ul className="list-unstyled text-body-secondary small">
              <li className="mb-1"><Link to="/docs" className="text-body-secondary text-decoration-none">Documentation</Link></li>
              <li className="mb-1"><Link to="/docs/api" className="text-body-secondary text-decoration-none">Document API</Link></li>
              <li className="mb-1"><Link to="/docs/sdks" className="text-body-secondary text-decoration-none">Sessions &amp; SDKs</Link></li>
              <li className="mb-1"><a href={API_REFERENCE_URL} target="_blank" rel="noopener noreferrer" className="text-body-secondary text-decoration-none">API Reference</a></li>
            </ul>
          </div>
          <div className="col-6 col-lg-2">
            <h6 className="fw-semibold mb-3">Company</h6>
            <ul className="list-unstyled text-body-secondary small">
              <li className="mb-1"><span className="text-body-secondary">About</span></li>
              <li className="mb-1"><span className="text-body-secondary">Blog</span></li>
              <li className="mb-1"><a href={section(onLanding, '#contact')} className="text-body-secondary text-decoration-none">Contact</a></li>
            </ul>
          </div>
          <div className="col-6 col-lg-2">
            <h6 className="fw-semibold mb-3">Legal</h6>
            <ul className="list-unstyled text-body-secondary small">
              <li className="mb-1"><span className="text-body-secondary">Privacy</span></li>
              <li className="mb-1"><span className="text-body-secondary">Terms</span></li>
              <li className="mb-1"><span className="text-body-secondary">Security</span></li>
            </ul>
          </div>
        </div>
        <hr className="my-4" />
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <small className="text-body-secondary">© {new Date().getFullYear()} InertiaID Platform. All rights reserved.</small>
          <div className="d-flex gap-3">
            <span className="badge bg-body-secondary text-body">KYC / KYB</span>
            <span className="badge bg-body-secondary text-body">AML</span>
            <span className="badge bg-body-secondary text-body">GDPR Ready</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
